import React, { useEffect, useRef, useState } from "react";
import { ABtn, AResult, AScore } from "./arcadeUi";
import { sfx } from "@/lib/soundfx";

// Rhythm game: jump as the rope sweeps under your feet. Combo builds, speed rises.
export default function Skipping() {
  const [phase, setPhase] = useState("ready"); // ready | play | over
  const [t, setT] = useState(0);          // rope angle 0..1 (0 = at feet)
  const [combo, setCombo] = useState(0);
  const [best, setBest] = useState(0);
  const [lives, setLives] = useState(3);
  const [flash, setFlash] = useState(null);
  const speed = useRef(0.45);   // rope loops per second (time-based, framerate-independent)
  const raf = useRef(null);
  const jumping = useRef(false);
  const scored = useRef(false);
  const tRef = useRef(0);
  const last = useRef(0);

  const loop = (ts) => {
    if (!last.current) last.current = ts;
    const dt = Math.min(0.05, (ts - last.current) / 1000);
    last.current = ts;
    setT((v) => {
      let nv = v + speed.current * dt;
      if (nv >= 1) {
        nv -= 1;
        // rope passed the feet — did the player clear it?
        if (!scored.current) miss();
        scored.current = false;
      }
      tRef.current = nv;
      return nv;
    });
    raf.current = requestAnimationFrame(loop);
  };

  const start = () => {
    setCombo(0); setLives(3); setT(0); setFlash(null);
    speed.current = 0.45; scored.current = false; jumping.current = false; tRef.current = 0; last.current = 0;
    setPhase("play");
  };

  const miss = () => {
    setFlash("miss"); setCombo(0);
    setLives((l) => {
      const nl = l - 1;
      if (nl <= 0) { cancelAnimationFrame(raf.current); setPhase("over"); }
      return nl;
    });
    setTimeout(() => setFlash(null), 250);
  };

  const jump = () => {
    if (phase !== "play" || jumping.current) return;
    jumping.current = true;
    setTimeout(() => { jumping.current = false; }, 320);
    // window when rope is near feet (t close to 0 or 1)
    const near = tRef.current < 0.2 || tRef.current > 0.8;
    if (near && !scored.current) {
      scored.current = true;
      setFlash("hit"); sfx.hit?.();
      setCombo((c) => { const nc = c + 1; setBest((b) => Math.max(b, nc)); if (nc % 5 === 0) speed.current += 0.08; return nc; });
      setTimeout(() => setFlash(null), 180);
    }
  };

  // rope RAF loop — its own effect so it isn't cancelled every frame by t changes
  useEffect(() => {
    if (phase !== "play") return undefined;
    raf.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  useEffect(() => {
    const onKey = (e) => { if (e.code === "Space") { e.preventDefault(); jump(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // rope sweeps in a full loop: at feet when t≈0/1, overhead at t≈0.5 (jump when it reaches your feet)
  const ropeLift = (1 - Math.cos(t * Math.PI * 2)) / 2; // 0 = feet, 1 = overhead
  const atFeet = t < 0.2 || t > 0.8;

  return (
    <div className="w-full text-center">
      <div className="flex justify-center gap-2">
        <AScore label="Combo" value={combo} />
        <AScore label="Best" value={best} />
        <AScore label="Lives" value={"❤".repeat(Math.max(0, lives)) || "—"} />
      </div>
      <p className="my-3 text-sm text-kirana-brown/70">Tap JUMP (or press Space) right as the rope reaches your feet. Ek… do… teen!</p>

      <div data-testid="skip-scene" className={`relative mx-auto mb-3 h-48 w-full max-w-md overflow-hidden rounded-md border-2 border-kirana-border ${flash === "hit" ? "bg-kirana-green/25" : flash === "miss" ? "bg-kirana-red/25" : "bg-gradient-to-b from-[#bfe3ff] to-[#eaf6ff]"}`}>
        <div className="absolute inset-x-0 bottom-0 h-8 bg-[#8FBF6B]" />
        {/* the kid */}
        <div className={`absolute bottom-8 left-1/2 -translate-x-1/2 transition-transform duration-150 ${jumping.current ? "-translate-y-7" : ""}`}>
          <div className="mx-auto h-6 w-6 rounded-full bg-[#F1C27D] border-2 border-[#7a5230]" />
          <div className="mx-auto -mt-1 h-8 w-5 rounded-sm bg-kirana-red" />
        </div>
        {/* rope arc sweeping up and down */}
        <div
          className={`absolute left-1/2 h-2 w-44 -translate-x-1/2 rounded-full transition-colors ${atFeet ? "bg-kirana-red" : "bg-[#3a2617]"}`}
          style={{ bottom: `${8 + ropeLift * 78}%`, borderRadius: "50%" }}
        />
        {/* feet target zone — jump when rope glows here */}
        <div className={`absolute inset-x-0 bottom-8 h-1.5 transition-colors ${atFeet ? "bg-kirana-red/80" : "bg-kirana-gold/50"}`} />
      </div>

      <div className="mt-4">
        {phase === "ready" && <ABtn data-testid="skip-start" onClick={start} tone="gold" className="px-8 py-3">Start Skipping</ABtn>}
        {phase === "play" && <ABtn data-testid="skip-jump" onClick={jump} tone="red" className="px-12 py-4 text-lg">JUMP! 🦵</ABtn>}
        {phase === "over" && (
          <>
            <AResult testId="skip-result" win={best >= 10} text={`${best} jumps in a row!${best >= 10 ? " Champion! 🏆" : ""}`} />
            <div className="mt-3 flex justify-center"><ABtn data-testid="skip-again" onClick={start} tone="green">Play Again</ABtn></div>
          </>
        )}
      </div>
    </div>
  );
}
